import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Target, ClipboardCheck, FlaskConical, BarChart3 } from "lucide-react";
import { Leaf, Burst, HandUnderline } from "@/components/home/Doodles";
import { PHOTOS, Photo } from "@/components/home/photos";
import { CtaButton } from "@/components/home/HeroSection";

export function AanpakSection() {
  return (
    <section className="bg-ll-cream py-6 sm:py-10" data-testid="home-aanpak">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6">
        <div className="group relative lg:col-span-5 rounded-[28px] bg-white/80 border border-ll-forest/10 p-7 sm:p-10 overflow-hidden">
          <Leaf className="absolute top-6 right-6 w-20 text-ll-leaf" />
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-ll-ink/70">Onze aanpak</p>
          <h2 className="mt-4 font-heading font-semibold text-4xl sm:text-5xl leading-[1.02] text-ll-forest pr-20">
            Van stilstand naar <span className="relative inline-block">speelruimte.<HandUnderline className="absolute -bottom-2 left-0 w-full h-3 text-ll-orange" /></span>
          </h2>
          <p className="mt-6 text-ll-ink/85 leading-relaxed">Wij geloven in de kracht van aandacht, vertrouwen en doen.</p>
          <p className="mt-3 text-ll-ink/85 leading-relaxed">Geen standaardtraject, maar een aanpak die aansluit bij de mens achter de situatie.</p>
          <p className="mt-3 text-ll-ink/85 leading-relaxed">Bij LevelLeven ontstaat ruimte om opnieuw te ontdekken wat mogelijk is.</p>
          <Link to="/methode" data-testid="home-aanpak-cta" className="group/l mt-5 py-3 min-h-[44px] inline-flex items-center gap-2 font-semibold text-ll-orange hover:text-ll-forest transition-colors">
            Lees meer over onze aanpak <ArrowRight size={17} className="transition-transform group-hover/l:translate-x-1" />
          </Link>
        </div>
        <div className="relative lg:col-span-7 min-h-[280px] sm:min-h-[400px] rounded-[28px] overflow-hidden lift">
          <Photo photo={PHOTOS.pad} sizes="(min-width:1024px) 58vw, 100vw" className="absolute inset-0 w-full h-full" />
        </div>
      </div>
    </section>
  );
}

const TRUST = [
  { icon: BookOpen, t: "Theoretische onderbouwing", d: "De aanpak bouwt op bestaande inzichten over motivatie, eigen regie en groepsleren. Met bronnen.", s: "Beschikbaar" },
  { icon: Target, t: "Beoogde resultaten", d: "Meer zelfinzicht, eigen regie, vaardigheden en een passende vervolgstap. Doelen, geen beloftes.", s: "Vastgelegd" },
  { icon: ClipboardCheck, t: "Vastlegging van ontwikkeling", d: "Start-, tussen- en eindmeting, waar afgesproken een vervolgmeting.", s: "In gebruik" },
  { icon: FlaskConical, t: "Onderzoek en validatie", d: "We willen de aanpak in pilots laten toetsen, bij voorkeur met een onafhankelijke partner. Nog niet gevalideerd.", s: "Ambitie" },
  { icon: BarChart3, t: "Gerealiseerde resultaten", d: "Die publiceren we zodra ze aantoonbaar zijn. Tot die tijd noemen we geen percentages.", s: "Volgt" },
];

export function TrustSection() {
  return (
    <section className="bg-ll-cream py-16 sm:py-24" data-testid="home-trust">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
          <div className="lg:col-span-7">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-ll-ink/70">Bewijs en vertrouwen <Burst className="w-6 h-6 text-ll-orange" /></p>
            <h2 className="mt-4 font-heading font-semibold text-4xl sm:text-5xl leading-[1.04] text-ll-forest">Zorgvuldig onderbouwd. Eerlijk over wat we nog niet weten.</h2>
          </div>
          <p className="lg:col-span-5 text-ll-ink/80 leading-relaxed">
            LevelLeven werkt aan een aanpak die je kunt volgen en meten. We maken duidelijk onderscheid tussen wat al vaststaat en wat we nog willen aantonen.
          </p>
        </div>
        <ol className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {TRUST.map((x, i) => {
            const Icon = x.icon;
            return (
              <motion.li key={x.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.07 }} className="lift rounded-3xl bg-white border border-ll-forest/10 p-6 flex flex-col" data-testid={`home-trust-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="w-11 h-11 rounded-full bg-ll-mist text-ll-leaf flex items-center justify-center"><Icon size={20} /></span>
                  <span className={`text-[11px] font-semibold uppercase tracking-[0.14em] rounded-full px-2.5 py-1 ${i < 3 ? "bg-ll-mist text-ll-forest" : "bg-ll-peach text-ll-orange"}`}>{x.s}</span>
                </div>
                <p className="mt-5 font-heading font-semibold text-lg text-ll-forest leading-snug">{x.t}</p>
                <p className="mt-2 text-sm text-ll-ink/75 leading-relaxed">{x.d}</p>
              </motion.li>
            );
          })}
        </ol>
        <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center">
          <CtaButton to="/methode" variant="forest" testid="home-trust-cta">Hoe wij ontwikkeling inzichtelijk maken</CtaButton>
          <Link to="/onderzoek-en-onderbouwing" data-testid="home-trust-onderzoek" className="inline-flex items-center min-h-[44px] text-sm font-semibold text-ll-forest underline underline-offset-4 decoration-ll-orange hover:text-ll-orange">
            Bekijk de onderzoeken en bronnen
          </Link>
        </div>
      </div>
    </section>
  );
}

export function CTASection() {
  return (
    <section className="bg-ll-cream pb-20 sm:pb-28 pt-4" data-testid="home-cta">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="relative rounded-[32px] bg-ll-forest text-white overflow-hidden px-7 py-12 sm:px-14 sm:py-16">
          <span className="absolute -top-20 -right-16 w-72 h-72 rounded-[45%] bg-ll-leaf/40" aria-hidden="true" />
          <span className="absolute -bottom-24 right-1/4 w-64 h-64 rounded-[40%] bg-ll-sage/15" aria-hidden="true" />
          <Burst className="absolute top-8 right-10 w-10 h-10 text-ll-orange" />
          <Leaf className="absolute bottom-6 right-8 w-24 text-ll-sage hidden sm:block" />
          <div className="relative max-w-2xl">
            <h2 className="font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">Klaar voor een nieuw begin?</h2>
            <p className="mt-5 text-lg text-white/85 leading-relaxed">
              Ontdek of het START-programma past bij jouw situatie, of bij de inwoners van jouw gemeente.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <CtaButton to="/contact" testid="home-cta-gemeente">Bespreek een gemeentelijke pilot</CtaButton>
              <CtaButton to="/voor-jou" variant="light" testid="home-cta-deelnemer">Ontdek START als deelnemer</CtaButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
